import React, { useEffect, useState } from 'react'
import { Box, Typography, Icon, Divider, CardMedia } from '@mui/material'
import { format, parseISO, addDays, subDays, differenceInYears, isPast, isToday } from 'date-fns'

// Local Imports
import AdxDrawerComponent from 'components/reusable/drawer/AdxDrawerComponent'
import defaultAvatar from 'assets/images/default_avatar.png'
import { useDailyDate } from 'services/hooks/useDate'

const TalentCalendarDrawer = ({
  state,
  handleDrawer,
  handleModal,
  calendarEvent,
  dateData,
  setData,
}) => {
  const { day } = useDailyDate({ date: dateData })
  const [interviewSlot, setInterviewSlot] = useState([])
  const dayOfWeek = day?.object?.toLocaleDateString('en-US', { weekday: 'long' })

  useEffect(() => {
    if (day) {
      const interviewData =
        calendarEvent?.length > 0 &&
        calendarEvent?.find((item) => format(parseISO(item[0]), 'dd MMMM yyyy') === day?.fullDate)
      if (interviewData) {
        setInterviewSlot(interviewData[1])
      } else {
        setInterviewSlot([])
      }
    }
  }, [day, calendarEvent])

  const handleClick = (itemId) => {
    setData(itemId)
    handleModal()
  }

  const HeaderProps = () => {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', lg: 'row' },
          alignItems: { xs: 'flex-start', lg: 'center' },
          columnGap: 2,
          rowGap: 1,
        }}
      >
        <Typography
          variant="subtitle1"
          color="dark-text.main"
          sx={{ fontWeight: '600', width: '400px' }}
        >
          {`${dayOfWeek}, ${day?.fullDate}`}
        </Typography>
      </Box>
    )
  }
  return (
    <AdxDrawerComponent
      state={state}
      anchor="right"
      onClose={handleDrawer}
      headerprops
      title={`${dayOfWeek}, ${day?.fullDate}`}
    >
      {interviewSlot?.map((item, index) => {
        const status = item?.status
        const capitalizedStatus = status ? status.charAt(0).toUpperCase() + status.slice(1) : null

        const formattedStartTime = new Date(`2000-01-01T${item?.start_time}`).toLocaleTimeString(
          'en-US',
          {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
          }
        )

        const formattedEndTime = new Date(`2000-01-01T${item?.end_time}`).toLocaleTimeString(
          'en-US',
          {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
          }
        )

        const currentTime = new Date()
        const dateTimeString = `${item?.end_date}T${item?.end_time}`
        const endDateTimeObject = new Date(dateTimeString)

        const checkIsPast = (date) => {
          return currentTime > date
        }
        const pendingStatus = checkIsPast(endDateTimeObject)

        return (
          <Box
            key={index}
            p="24px"
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            gap="22px"
            sx={{
              width: '100%',
              cursor: 'pointer',
            }}
            borderBottom={1}
            borderColor="gray-line.main"
            onClick={() => handleClick(item?.id)}
          >
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              gap="20px"
              sx={{ width: '100%' }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography fontWeight="600" variant="h4" color="dark-text.main">
                  {`${item?.title} (${item?.relations?.candidateable?.relations?.candidateable?.name}) `}
                </Typography>
              </Box>
              <Box
                display="flex"
                gap="12px"
                alignItems="center"
                sx={{
                  borderRadius: '4.5px',
                  paddingX: 1,
                  paddingY: 0.5,
                  backgroundColor:
                    item?.status === 'scheduled' && pendingStatus ? 'light-error.main' : null,
                }}
              >
                {item?.status === 'scheduled' && !pendingStatus ? (
                  <>
                    <Icon sx={{ fontSize: '16px', color: 'light-gray-text.main' }}>
                      calendar_month
                    </Icon>
                  </>
                ) : item?.status === 'scheduled' && pendingStatus ? (
                  <>
                    <Icon sx={{ fontSize: '16px', color: 'warning.main' }}>error</Icon>
                  </>
                ) : item?.status === 'cancelled' ? (
                  <>
                    <Icon sx={{ fontSize: '16px', color: 'error.main' }}>cancel</Icon>
                  </>
                ) : item?.status === 'candidate-no-show' ? (
                  <>
                    <Icon sx={{ fontSize: '16px', color: 'warning.main' }}>help</Icon>
                  </>
                ) : (
                  <>
                    <Icon sx={{ fontSize: '16px', color: 'positive.main' }}>check_circle</Icon>
                  </>
                )}

                <Typography variant="body1" color="light-gray-text.main">
                  {item?.status === 'scheduled' && !pendingStatus ? (
                    <>{`${dayOfWeek}, ${day?.fullDate}`}</>
                  ) : item?.status === 'scheduled' && pendingStatus ? (
                    <>
                      <Typography>Pending action required</Typography>
                    </>
                  ) : (
                    <>{capitalizedStatus}</>
                  )}
                </Typography>
                <Typography variant="body1" color="light-gray-text.main">
                  •
                </Typography>
                <Typography variant="body1" color="light-gray-text.main">
                  {`${formattedStartTime} - ${formattedEndTime}`}
                </Typography>
              </Box>
              <Box
                bgcolor="default-background.main"
                display="flex"
                alignItems="center"
                padding="12px"
              >
                <CardMedia
                  component="img"
                  sx={{
                    marginRight: 2,
                    height: 45,
                    width: 45,
                    objectFit: 'cover',
                    borderRadius: '50%',
                  }}
                  src={
                    item?.relations?.candidateable?.relations?.candidateable?.profile_image_url ||
                    defaultAvatar
                  }
                />
                <Box sx={{ width: '100%' }}>
                  <Typography variant="body1" color="dark-text.main" fontWeight="600">
                    {item?.relations?.candidateable?.relations?.candidateable?.name}
                  </Typography>
                  <Typography variant="body2" color="light-gray-text.main">
                    {item?.relations?.candidateable?.relations?.candidateable?.email}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        )
      })}
    </AdxDrawerComponent>
  )
}

export default TalentCalendarDrawer
